import type { LogoutOptions } from '@auth0/auth0-react';

import { postToNative } from 'utils/native-container/bridge';

type Auth0Logout = (options?: LogoutOptions) => Promise<void>;

/**
 * Log out of Auth0, telling the mobile container first.
 *
 * `auth0Logout` replaces the document immediately, so nothing after it runs and
 * no component gets another render. `Auth0TokenHandler` announces a sign-out
 * when its user goes away, but on this path it never gets the chance: the page
 * is gone, and the handler remounts after the redirect with no memory of who
 * just left.
 *
 * The container therefore never hears about the logout, and the device stays
 * registered to that account. The next person to pick up the phone keeps
 * receiving the previous user's farm alerts.
 *
 * `USER_IDENTIFIED` does not rescue it. The container treats an identify as an
 * exclusive rebind, which covers an account *switch*, but these paths end in
 * nobody being signed in at all, so no identify ever follows.
 *
 * **Every Auth0 logout must go through here.** Four call sites did the same
 * thing independently and only one of them announced it; a fifth would have
 * missed it too. `__tests__/logout-call-sites.test.ts` fails the build if a new
 * one calls `logout` directly.
 *
 * A no-op in a normal browser.
 */
export function logoutAnnouncingToNative(
  auth0Logout: Auth0Logout,
  options?: LogoutOptions
): Promise<void> {
  postToNative({ type: 'USER_SIGNED_OUT' });
  return auth0Logout(options);
}
